// Inline-editable Overview sections for the project workspace.
//
// These mirror the (now-retired) project edit modal in ProjectsPage.tsx
// FAITHFULLY — same fields, input types, lookups, validation, and save payload.
// Each section is a self-contained .pw-card with its own local edit state and a
// per-section Edit / Save / Cancel toggle (shown only to users with
// `projects.edit`). Saves go through the existing PATCH /projects/:id, the notes
// endpoints, and DELETE /projects/:id — no backend changes.

import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { projectsApi, usersApi, lookupsApi, type LookupOption, type FieldLeader } from '@/lib/apiClient';
import { fmtDate, toDateInputValue, DATE_INPUT_MIN, DATE_INPUT_MAX } from '@/lib/dates';
import type { ProjectPhase, OverallCompletion } from '@/lib/schedule';
import { invalidateAddressQueries, invalidateAssignmentQueries, invalidateTravelJobQueries } from '@/lib/queryClient';
import { TRAVEL_BANDS, travelRotationLabel, type TravelBand } from '@/lib/travelBand';
import FieldLeaderPicker from './FieldLeaderPicker';
import JobAddressPicker from './JobAddressPicker';
import { restoreWarning } from './lib/restoreArchivedJob';

// ─── Types ──────────────────────────────────────────────────────────────────

interface NamedRef { id: number; label: string }
interface UserRef { id: string; displayName: string | null; email: string | null }

export interface ProjectNote {
  id: number;
  projectId: number;
  category: 'delay' | 'pm' | 'estimator';
  authorId: string | null;
  authorName: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
  edited: boolean;
  canEdit: boolean;   // author only
  canDelete: boolean; // author or Admin
}

// Full project detail returned by GET /projects/:id. Carries every field the
// editable Overview sections need — the same shape the edit modal consumed.
export interface OverviewProject {
  id: number;
  jobNumber: string;
  /** Set when the bid was marked Closed-Won in Bid Tracker. A SYSTAC- job with
   *  this set is "won, pending renumber" — editable (not edit-locked), still
   *  activity-locked until a real Sage #. */
  bidClosedWonAt?: string | null;
  /** The Bid Tracker's id for the bid this job came from or was linked to; null when it has none. */
  sourceBidId?: number | null;
  /** Proof of notice to proceed, recorded in Bid Tracker when the bid was won:
   *  a type (Contract, PO, LOI or Email NTP), a reference (a PO or contract
   *  number, or for an LOI or email a short description) and the date it was
   *  received. All null for a job that predates it. Read-only here: only the bid
   *  events write them, so no Overview edit form carries them. */
  ntpType?: string | null;
  ntpReference?: string | null;
  ntpReceivedOn?: string | null;
  title: string;
  client: string | null;
  /** Structured field-leader assignments (drive access) — from GET /projects/:id. */
  fieldLeaders?: FieldLeader[];
  contractValue: number | null;
  /** Estimator-entered labor hours + the missing-hours flag. See lib/laborHours. */
  /** Server-computed: is the Intacct contract missing on this job? See contract-visibility. */
  contractState?: 'set' | 'missing' | 'exempt';
  estimatedHours?: { effective: number | null; state?: 'set' | 'missing' | 'exempt' } | null;
  health: 'on-track' | 'at-risk' | 'off-track';
  /** Which rule decided `health`, and the sentence explaining it. Health is
   *  derived server-side, so these come from the same place the value does —
   *  the UI never re-implements the heuristic. */
  healthRule?: 'closed' | 'past-completion' | 'mob-delayed' | 'mob-off-track'
    | 'start-slipped' | 'clear' | 'no-dates' | null;
  healthReason?: string | null;
  projectStatus: NamedRef | null;
  mobilizationStatus: NamedRef | null;
  division: NamedRef | null;
  primaryPm: UserRef | null;
  secondaryPm: UserRef | null;
  projectEngineer: UserRef | null;
  estimator: UserRef | null;
  safetyManager: UserRef | null;
  originalStartDate: string | null;
  currentStartDate: string | null;
  estimatedCompletion: string | null;
  /** max(estimatedCompletion, latest mob end) — a remob running past the
   *  stored date supersedes it for display (derived server-side). */
  effectiveCompletion?: string | null;
  estimatorHandoffDate: string | null;
  fieldLeaderHandoffDate: string | null;
  currentDuration: number | null;
  originalDuration: number | null;
  bidPeakCrew: number | null;
  bidWeeklyHours: number | null;
  jobAddress: string | null;
  /** How much the stored address is trusted. See job-address.ts on the API. */
  jobAddressStatus: 'unverified' | 'verified' | 'unresolvable' | null;
  siteContactName: string | null;
  siteContactPhone: string | null;
  siteContactEmail: string | null;
  travelJob: boolean;
  /** Legacy free text. Displayed only when there is no band; never edited now. */
  travelDistanceNote: string | null;
  /** The per-diem band the estimator priced. One of TRAVEL_BANDS, or null. */
  travelBand: TravelBand | null;
  /** Subject to prevailing wage rates. A property of the JOB, not of the
   *  travel — a local project can be prevailing wage too. */
  isPrevailingWage: boolean;
  perDiem: boolean;
  /** The weekly ROTATION, 5 or 7 — not a count of per-diem days. */
  perDiemDays: number | null;
  clinicName: string | null;
  clinicPhone: string | null;
  clinicAddress: string | null;
  jobFolderUrl: string | null;
  isArchived: boolean;
  /** Intacct's "Job status" as the nightly sync last read it. Null until read (or blank
   *  in Intacct); absent from an API that predates the sync. */
  intacctJobStatus?: string | null;
  /** True when that status is Completed — decided by the API, which owns the rule. */
  intacctJobCompleted?: boolean;
  notes: ProjectNote[];
  /** Mobilization phases (GET /projects/:id) — drive the mob-end vs. plan
   *  divergence flag on the Schedule & Dates card. */
  mobilizations?: { id: number; startDate: string | null; endDate: string | null }[];
  // Per-phase schedule (Overview "Schedule & Dates" card) + blended completion.
  // Present on GET /projects/:id; absent for legacy/unsaved projects.
  phases?: ProjectPhase[];
  completion?: OverallCompletion;
  /** Intacct revised contract total (synced) — preferred contract source. */
  sageContractRevised?: number | null;
  /** Full Intacct contract breakdown (GET /projects/:id) for Change Mgmt. */
  sageContract?: {
    contractId: string | null;
    contractCount: number;
    original: number | null;
    approvedChange: number | null;
    pendingChange: number | null;
    revised: number | null;
    billed: number | null;
    syncedAt: string | null;
  } | null;
}

type UserItem = { id: string; displayName: string | null; email: string | null; deletedAt: string | null };

// ─── Lookups / constants (mirror ProjectsPage exactly) ──────────────────────

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },     { id: 2, label: 'Construction' }, { id: 3, label: 'Complete' },
  { id: 4, label: 'Canceled' },   { id: 5, label: 'On Hold' },      { id: 6, label: 'Schedule Only' },
  { id: 7, label: 'Pipeline' },   { id: 8, label: 'Preconstruction' },
];
/** Fallback only — the live list comes from /lookups/mobilization-statuses so a
 *  value added in the database is selectable without a frontend release. This
 *  used to be the hardcoded source of truth in TWO files, which is why adding
 *  MOBILIZED needed a code change at all. */
const MOB_OPTIONS_FALLBACK: LookupOption[] = [
  { id: 1, label: 'ON TRACK' }, { id: 2, label: 'DELAYED' }, { id: 3, label: 'OFF TRACK' },
];

export function useMobilizationOptions(): LookupOption[] {
  const { data } = useQuery({
    queryKey: ['lookups', 'mobilization-statuses'],
    queryFn: lookupsApi.listMobilizationStatuses,
    staleTime: 5 * 60 * 1000,
  });
  return data ?? MOB_OPTIONS_FALLBACK;
}
const DIV_OPTIONS_FALLBACK: LookupOption[] = [
  { id: 1, label: 'Industrial' },
  { id: 2, label: 'Commercial' },
  { id: 3, label: 'GC' },
  { id: 4, label: 'Fab' },
];

function useDivisionOptions(): LookupOption[] {
  const { data } = useQuery({
    queryKey: ['lookups', 'divisions'],
    queryFn: lookupsApi.listDivisions,
    staleTime: 5 * 60 * 1000,
  });
  return data ?? DIV_OPTIONS_FALLBACK;
}

const NOTE_CATEGORIES: { key: ProjectNote['category']; label: string }[] = [
  { key: 'estimator', label: 'Estimator Notes' },
  { key: 'pm',        label: 'PM Notes' },
  { key: 'delay',     label: 'Notes' },
];

// ─── Permissions ────────────────────────────────────────────────────────────

const isAdminRole = (r: string) => r.toLowerCase().replace(/\s+/g, '') === 'admin';

/** edit = `projects.edit` (Admin implies it). */
export function useCanEditProject(): boolean {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  return !!me && ((me.permissions ?? []).includes('projects.edit') || (me.roles ?? []).some(isAdminRole));
}

/**
 * Who may SUBMIT an RFI — `projects.edit` OR the narrower `rfis.create`.
 *
 * ⚠️ Deliberately wider than `useCanEditProject`, and only for creating. Field
 * leaders hold `rfis.create` so they can raise a question on their own job
 * (product owner, 2026-08-04); answering, sending to the assignee, editing and reopening
 * all remain on `projects.edit`, because RFIs are PM-owned.
 *
 * Mirrors `@RequirePermission('projects.edit', 'rfis.create')` on `POST /rfis`.
 * The API additionally requires the creator to be ASSIGNED to the project — this
 * hook can't know that, so a wrongly-shown button would fail with a clear 403
 * rather than appear to work.
 */
export function useCanCreateRfi(): boolean {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  return !!me && (
    (me.permissions ?? []).includes('projects.edit') ||
    (me.permissions ?? []).includes('rfis.create') ||
    (me.roles ?? []).some(isAdminRole)
  );
}

/** delete = `projects.delete` PERMISSION (the custom ProjectAdmin role carries it
 *  in addition to Admin) — gate on the permission, with Admin role as a fallback. */
function useCanDeleteProject(): boolean {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => usersApi.me(), staleTime: 5 * 60 * 1000 });
  return !!me && ((me.permissions ?? []).includes('projects.delete') || (me.roles ?? []).some(isAdminRole));
}

// ─── Shared styles (aligned with the workspace's .pw-card look) ─────────────

const css = `
  .ov-sec-hd {
    display: flex; align-items: center; justify-content: space-between; gap: var(--ew-s-3);
    padding: 14px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
  }
  .ov-sec-title {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin: 0;
  }
  /* The two button shapes that used to live here are now .ew-btn-outline and
     .ew-btn-link in styles/tokens.css — several other modules use them, and a
     block in here only exists while the Overview is mounted. */
  .ov-row {
    display: flex; justify-content: space-between; gap: var(--ew-s-4);
    padding: 10px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font);
  }
  .ov-row:last-child { border-bottom: 0; }
  .ov-row > span:first-child { color: var(--ew-n-500); flex-shrink: 0; }
  .ov-row > span:last-child { color: var(--ew-dark); font-weight: var(--ew-fw-medium); text-align: right; }
  .ov-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: var(--ew-s-3) var(--ew-s-4); padding: var(--ew-s-4); }
  .ov-field { display: flex; flex-direction: column; }
  .ov-field.full { grid-column: 1 / -1; }
  .ov-label {
    font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500); margin-bottom: 5px;
  }
  .ov-input {
    border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 7px 10px;
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); width: 100%;
    background: #fff; color: var(--ew-dark); font-family: inherit;
  }
  .ov-hint { font: var(--ew-fw-regular) 11px/1.3 var(--ew-font); color: var(--ew-n-500); margin-top: 4px; }
  .ov-actions { display: flex; justify-content: flex-end; gap: var(--ew-s-2); padding: var(--ew-s-3) var(--ew-s-4); border-top: 1px solid var(--ew-n-100); }
  .ov-err { margin: var(--ew-s-3) var(--ew-s-4) 0; padding: 8px 12px; background: #FBE4DF; color: #8F2414; border-radius: var(--ew-r-md); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
  .ov-note-input { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 6px 8px; font: var(--ew-fw-regular) 12px var(--ew-font); width: 100%; font-family: inherit; resize: vertical; }
`;

function personName(u: UserRef | null): string {
  return u?.displayName ?? u?.email ?? '—';
}

// ─── Per-section edit-state hook ────────────────────────────────────────────
//
// Each section keeps its own local string-keyed form (mirrors the modal's
// Record<string,string> form). `ne`/`ni` turn '' into null / null-or-number for
// the PATCH payload, exactly as the modal's handleSave did.

const ne = (v: string) => (v === '' ? null : v);
const ni = (v: string) => (v === '' ? null : Number(v));

function useSectionEdit(project: OverviewProject, init: (p: OverviewProject) => Record<string, string>) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(() => init(project));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = () => { setForm(init(project)); setError(null); setEditing(true); };
  const cancel = () => { setForm(init(project)); setError(null); setEditing(false); };
  const f = (k: string) => form[k] ?? '';
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  // Persist a payload via PATCH /projects/:id and refresh the project +
  // portfolio + rollups so navigating back shows the change.
  const save = useCallback(async (payload: Record<string, unknown>) => {
    setSaving(true);
    setError(null);
    try {
      await projectsApi.update(project.id, payload);
      qc.invalidateQueries({ queryKey: ['project-counts', project.id] });
      qc.invalidateQueries({ queryKey: ['kpi'] });
      // ⚠️ The travel_job family — the SAME list the crew schedule's Travel/Local
      // toggle uses, because this card writes the same column. It also covers
      // `project` / `projects`, which is why they are no longer repeated above.
      //
      // Called unconditionally rather than gated on `travelJob` appearing in the
      // payload. Several other fields edited on this tab are read by the crew
      // schedule on its own short clocks — current_start_date, bid_peak_crew and
      // the durations draw the "Needs Scheduling" section and the SYSTAC bid
      // forecast, which are crew NUMBERS on the grid rather than a label. Gating
      // would fix the visible half and leave the worse half stale. Invalidation
      // only refetches ACTIVE queries, so this costs nothing when nobody is
      // looking at a manpower screen.
      invalidateTravelJobQueries(qc, project.id);
      // ⚠️ This card writes the PM / estimator / engineer slots and the status,
      // so a save here can change whether the signed-in person is on any live
      // job at all — which is what the Today tab keys off. Unconditional for
      // the same reason as the line above: gating on the fields present would
      // be a second list to keep in step with the first.
      invalidateAssignmentQueries(qc, project.id);
      setEditing(false);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [project.id, qc]);

  return { editing, open, cancel, f, set, save, saving, error };
}

// A section shell: header with title + Edit toggle, then either view or edit body.
function SectionCard({
  title, canEdit, editing, onEdit, error, children,
}: {
  title: string; canEdit: boolean; editing: boolean; onEdit: () => void;
  error?: string | null; children: React.ReactNode;
}) {
  return (
    <div className="pw-card">
      <div className="ov-sec-hd">
        <h2 className="ov-sec-title">{title}</h2>
        {canEdit && !editing ? <button className="ew-btn-outline" onClick={onEdit}>Edit</button> : null}
      </div>
      {error ? <div className="ov-err">{error}</div> : null}
      {children}
    </div>
  );
}

function SaveCancel({ onCancel, onSave, saving }: { onCancel: () => void; onSave: () => void; saving: boolean }) {
  return (
    <div className="ov-actions">
      <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="ov-row"><span>{label}</span><span>{value ?? '—'}</span></div>;
}

// ─── Basics ─────────────────────────────────────────────────────────────────

export function BasicsSection({ project, canEdit }: { project: OverviewProject; canEdit: boolean }) {
  const divisionOptions = useDivisionOptions();
  const mobOptions = useMobilizationOptions();
  const s = useSectionEdit(project, (p) => ({
    title: p.title ?? '',
    jobNumber: p.jobNumber ?? '',
    client: p.client ?? '',
    projectStatusId: String(p.projectStatus?.id ?? ''),
    mobilizationStatusId: String(p.mobilizationStatus?.id ?? ''),
    divisionId: String(p.division?.id ?? ''),
  }));

  const onSave = () => s.save({
    title: s.f('title') || undefined,
    jobNumber: s.f('jobNumber').trim() || undefined,
    client: ne(s.f('client')),
    projectStatusId: s.f('projectStatusId') ? Number(s.f('projectStatusId')) : undefined,
    mobilizationStatusId: s.f('mobilizationStatusId') ? Number(s.f('mobilizationStatusId')) : null,
    divisionId: s.f('divisionId') ? Number(s.f('divisionId')) : null,
  });

  return (
    <SectionCard title="Basics" canEdit={canEdit} editing={s.editing} onEdit={s.open} error={s.error}>
      {!s.editing ? (
        <>
          <Row label="Title" value={project.title} />
          <Row label="Job #" value={project.jobNumber} />
          <Row label="Client" value={project.client} />
          <Row label="Project status" value={project.projectStatus?.label} />
          <Row label="Mob status" value={project.mobilizationStatus?.label} />
          <Row label="Division" value={project.division?.label} />
        </>
      ) : (
        <>
          <div className="ov-grid2">
            <div className="ov-field"><label className="ov-label">Title</label><input className="ov-input" value={s.f('title')} onChange={s.set('title')} /></div>
            <div className="ov-field"><label className="ov-label">Job #</label><input className="ov-input" value={s.f('jobNumber')} onChange={s.set('jobNumber')} /></div>
            <div className="ov-field"><label className="ov-label">Client</label><input className="ov-input" value={s.f('client')} onChange={s.set('client')} /></div>
            <div className="ov-field"><label className="ov-label">Project Status</label>
              <select className="ov-input" value={s.f('projectStatusId')} onChange={s.set('projectStatusId')}>
                <option value="">-- Select --</option>
                {STATUS_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="ov-field"><label className="ov-label">Mob Status</label>
              <select className="ov-input" value={s.f('mobilizationStatusId')} onChange={s.set('mobilizationStatusId')}>
                <option value="">-- None --</option>
                {mobOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="ov-field"><label className="ov-label">Division</label>
              <select className="ov-input" value={s.f('divisionId')} onChange={s.set('divisionId')}>
                <option value="">-- None --</option>
                {divisionOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <SaveCancel onCancel={s.cancel} onSave={onSave} saving={s.saving} />
        </>
      )}
    </SectionCard>
  );
}

// ─── Team ───────────────────────────────────────────────────────────────────

const TEAM_FIELDS: [string, string][] = [
  ['primaryPmId', 'Primary PM'], ['secondaryPmId', 'Secondary PM'],
  ['projectEngineerId', 'Project Engineer'], ['estimatorId', 'Estimator'],
  ['safetyManagerId', 'Safety Manager'],
];

/**
 * Options for a person select: everyone still here, PLUS whoever is already
 * chosen even if they have since been removed.
 *
 * ⚠️ THE `selectedId` CLAUSE IS LOAD-BEARING. A <select> whose value matches no
 * <option> renders BLANK (selectedIndex -1). Drop a removed PM from the list
 * and the Team form claims the job has no PM while it actually has one — and
 * the obvious response to that is to pick somebody, which is how the job
 * quietly changes hands.
 *
 * Saving untouched is safe on its own: `useSectionEdit` seeds its state from
 * the project and only updates it on a real onChange, so an unedited select
 * still submits the original id. The danger is the misleading display, not the
 * write. Keeping the person visible and labelled "(removed)" makes the actual
 * state legible instead of inviting the fix.
 */
function optionsFor(users: UserItem[], selectedId: string): UserItem[] {
  return users.filter((u) => !u.deletedAt || u.id === selectedId);
}

export function TeamSection({ project, canEdit, users }: { project: OverviewProject; canEdit: boolean; users: UserItem[] }) {
  const s = useSectionEdit(project, (p) => ({
    primaryPmId: p.primaryPm?.id ?? '',
    secondaryPmId: p.secondaryPm?.id ?? '',
    projectEngineerId: p.projectEngineer?.id ?? '',
    estimatorId: p.estimator?.id ?? '',
    safetyManagerId: p.safetyManager?.id ?? '',
  }));

  const onSave = () => s.save({
    primaryPmId: ne(s.f('primaryPmId')),
    secondaryPmId: ne(s.f('secondaryPmId')),
    projectEngineerId: ne(s.f('projectEngineerId')),
    estimatorId: ne(s.f('estimatorId')),
    safetyManagerId: ne(s.f('safetyManagerId')),
  });

  return (
    <SectionCard title="Team" canEdit={canEdit} editing={s.editing} onEdit={s.open} error={s.error}>
      {!s.editing ? (
        <>
          <Row label="Primary PM" value={personName(project.primaryPm)} />
          <Row label="Secondary PM" value={personName(project.secondaryPm)} />
          <Row label="Project Engineer" value={personName(project.projectEngineer)} />
          <Row label="Estimator" value={personName(project.estimator)} />
          <Row label="Safety Manager" value={personName(project.safetyManager)} />
        </>
      ) : (
        <>
          <div className="ov-grid2">
            {TEAM_FIELDS.map(([k, l]) => (
              <div key={k} className="ov-field"><label className="ov-label">{l}</label>
                <select className="ov-input" value={s.f(k)} onChange={s.set(k)}>
                  <option value="">-- None --</option>
                  {optionsFor(users, s.f(k)).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName ?? u.email}{u.deletedAt ? ' (removed)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <SaveCancel onCancel={s.cancel} onSave={onSave} saving={s.saving} />
        </>
      )}
      {/* Field leaders are a separate many-to-many assignment (their own
          endpoints), so the picker manages its own state and isn't part of the
          section's single-save form. Always visible; add/remove gated by canEdit. */}
      <FieldLeaderPicker projectId={project.id} canEdit={canEdit} jobFolderUrl={project.jobFolderUrl} />
    </SectionCard>
  );
}

// ─── Bid & Handoff ───────────────────────────────────────────────────────────
// Was "Schedule". The execution schedule (start/end/duration/completion) now
// lives in the per-phase "Schedule & Dates" card, which writes back the legacy
// project-level dates — so this section keeps only the estimator/FL handoff
// milestones and the bid crew figures, which the card doesn't own.

export function ScheduleSection({ project, canEdit }: { project: OverviewProject; canEdit: boolean }) {
  const s = useSectionEdit(project, (p) => ({
    estimatorHandoffDate: toDateInputValue(p.estimatorHandoffDate),
    fieldLeaderHandoffDate: toDateInputValue(p.fieldLeaderHandoffDate),
    bidPeakCrew: String(p.bidPeakCrew ?? ''),
    bidWeeklyHours: String(p.bidWeeklyHours ?? ''),
  }));

  const onSave = () => s.save({
    estimatorHandoffDate: ne(s.f('estimatorHandoffDate')),
    fieldLeaderHandoffDate: ne(s.f('fieldLeaderHandoffDate')),
    bidPeakCrew: ni(s.f('bidPeakCrew')),
    bidWeeklyHours: ni(s.f('bidWeeklyHours')),
  });

  const dateFields: [string, string][] = [
    ['estimatorHandoffDate', 'Estimator Handoff'], ['fieldLeaderHandoffDate', 'FL Handoff'],
  ];
  const numFields: [string, string][] = [
    ['bidPeakCrew', 'Peak Crew (Bid)'], ['bidWeeklyHours', 'Bid Weekly Hours'],
  ];

  return (
    <SectionCard title="Bid & Handoff" canEdit={canEdit} editing={s.editing} onEdit={s.open} error={s.error}>
      {!s.editing ? (
        <>
          <Row label="Estimator Handoff" value={fmtDate(project.estimatorHandoffDate)} />
          <Row label="FL Handoff" value={fmtDate(project.fieldLeaderHandoffDate)} />
          <Row label="Peak Crew (Bid)" value={project.bidPeakCrew} />
          <Row label="Bid Weekly Hours" value={project.bidWeeklyHours ? `${project.bidWeeklyHours} hrs` : null} />
          <div style={{ padding: '10px var(--ew-s-4)', font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)' }}>
            Start / end dates &amp; completion are managed in the Schedule &amp; Dates card above.
          </div>
        </>
      ) : (
        <>
          <div className="ov-grid2">
            {dateFields.map(([k, l]) => (
              <div key={k} className="ov-field"><label className="ov-label">{l}</label>
                <input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} className="ov-input" value={s.f(k)} onChange={s.set(k)} />
              </div>
            ))}
            {numFields.map(([k, l]) => (
              <div key={k} className="ov-field"><label className="ov-label">{l}</label>
                <input type="number" min={0} className="ov-input" value={s.f(k)} onChange={s.set(k)} />
              </div>
            ))}
          </div>
          <SaveCancel onCancel={s.cancel} onSave={onSave} saving={s.saving} />
        </>
      )}
    </SectionCard>
  );
}

// ─── Notice to proceed ──────────────────────────────────────────────────────
//
// How the job was awarded, as entered in Bid Tracker when the bid was won. Read
// only: the Project Tracker never writes it, so a correction is made in Bid
// Tracker and carried over. It is a plain card rather than a SectionCard because
// SectionCard exists to host an Edit toggle and this has nothing to edit.
//
// Renders nothing at all for a job with no proof recorded (every job that predates
// it, and any job that did not come from a bid), rather than an empty card of dashes.

/** What the reference means, by type. LOIs and emails have no number to quote, so
 *  theirs is a description; anything this list does not know stays a plain "Reference". */
function ntpReferenceLabel(type: string | null): string {
  switch (type?.trim().toLowerCase()) {
    case 'contract': return 'Contract #';
    case 'po': return 'PO #';
    case 'loi':
    case 'email ntp': return 'Details';
    default: return 'Reference';
  }
}

export function NoticeToProceedSection({ project }: { project: OverviewProject }) {
  const type = project.ntpType?.trim() || null;
  const reference = project.ntpReference?.trim() || null;
  const received = project.ntpReceivedOn || null;
  if (!type && !reference && !received) return null;

  return (
    <div className="pw-card">
      <div className="ov-sec-hd"><h2 className="ov-sec-title">Notice to proceed</h2></div>
      {type ? <Row label="Type" value={type} /> : null}
      {reference ? (
        // The reference can be a sentence typed by an estimator; let it wrap
        // rather than push the card wider.
        <Row label={ntpReferenceLabel(type)} value={<span style={{ overflowWrap: 'anywhere' }}>{reference}</span>} />
      ) : null}
      {received ? <Row label="Received" value={fmtDate(received)} /> : null}
      <div style={{ padding: '10px var(--ew-s-4)', font: 'var(--ew-fw-regular) var(--ew-fs-meta) var(--ew-font)', color: 'var(--ew-n-400)' }}>
        Recorded in Bid Tracker when the bid was won. To correct it, change it there.
      </div>
    </div>
  );
}

// ─── Job Info ───────────────────────────────────────────────────────────────

export function JobInfoSection({ project, canEdit }: { project: OverviewProject; canEdit: boolean }) {
  const qc = useQueryClient();
  const s = useSectionEdit(project, (p) => ({
    jobFolderUrl: p.jobFolderUrl ?? '',
    // ⚠️ jobAddress is deliberately ABSENT. The picker owns it and commits on
    // pick, because the address carries coordinates this card's generic PATCH
    // cannot write. Holding a copy here caused two bugs (Codex, #479): every
    // save re-sent the field and so invalidated a perfectly good verification,
    // and a value captured when the card opened overwrote an address picked
    // during the same session.
    siteContactName: p.siteContactName ?? '',
    siteContactPhone: p.siteContactPhone ?? '',
    siteContactEmail: p.siteContactEmail ?? '',
    travelJob: p.travelJob ? 'true' : 'false',
    travelBand: p.travelBand ?? '',
    isPrevailingWage: p.isPrevailingWage ? 'true' : 'false',
    perDiem: p.perDiem ? 'true' : 'false',
    perDiemDays: String(p.perDiemDays ?? ''),
    clinicName: p.clinicName ?? '',
    clinicPhone: p.clinicPhone ?? '',
    clinicAddress: p.clinicAddress ?? '',
  }));

  const onSave = () => s.save({
    jobFolderUrl: ne(s.f('jobFolderUrl').trim()),
    siteContactName: ne(s.f('siteContactName')),
    siteContactPhone: ne(s.f('siteContactPhone')),
    siteContactEmail: ne(s.f('siteContactEmail')),
    travelJob: s.f('travelJob') === 'true',
    // ⚠️ `travelDistanceNote` is deliberately NOT sent. The legacy free text is
    // read-only now; sending it back would rewrite history this PR promised to
    // leave alone, and the API would accept it.
    travelBand: ne(s.f('travelBand')),
    isPrevailingWage: s.f('isPrevailingWage') === 'true',
    perDiem: s.f('perDiem') === 'true',
    perDiemDays: ni(s.f('perDiemDays')),
    clinicName: ne(s.f('clinicName')),
    clinicPhone: ne(s.f('clinicPhone')),
    clinicAddress: ne(s.f('clinicAddress')),
  });

  return (
    <SectionCard title="Job Info" canEdit={canEdit} editing={s.editing} onEdit={s.open} error={s.error}>
      {!s.editing ? (
        <>
          <Row label="Job folder" value={project.jobFolderUrl
            ? <a href={project.jobFolderUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--ew-green)' }}>Open ↗</a>
            : null} />
          <Row label="Job address" value={project.jobAddress} />
          <Row label="Site contact" value={project.siteContactName} />
          <Row label="Site phone" value={project.siteContactPhone
            ? <a href={`tel:${project.siteContactPhone}`} style={{ color: 'var(--ew-green)' }}>{project.siteContactPhone}</a> : null} />
          <Row label="Site email" value={project.siteContactEmail
            ? <a href={`mailto:${project.siteContactEmail}`} style={{ color: 'var(--ew-green)' }}>{project.siteContactEmail}</a> : null} />
          <Row label="Travel job" value={project.travelJob ? 'Yes' : 'No'} />
          {/* ⚠️ The band when set; the legacy note only as a fallback. The old
              free-text field is never written again, but 36 historical jobs still
              hold text in it, so it stays readable until they age out. */}
          {project.travelBand
            ? <Row label="Travel band" value={`${project.travelBand} miles`} />
            : project.travelDistanceNote
              ? <Row label="Travel note" value={project.travelDistanceNote} />
              : null}
          {/* Shown only when set. It is rare, and a "No" on every job would be
              noise — but when it IS set it changes what a traveller is owed, so
              it reads as a statement rather than a field. */}
          {project.isPrevailingWage
            ? <Row label="Prevailing wage" value="Yes — no meals per diem or hourly increase" />
            : null}
          <Row label="Per diem" value={project.perDiem ? 'Yes' : 'No'} />
          {/* ⚠️ NOT "Yes — 7 days", which read as seven days OF per diem. */}
          {project.perDiemDays
            ? <Row label="Travel rotation" value={travelRotationLabel(project.perDiemDays)} />
            : null}
          <Row label="Clinic" value={project.clinicName} />
          <Row label="Clinic phone" value={project.clinicPhone
            ? <a href={`tel:${project.clinicPhone}`} style={{ color: 'var(--ew-green)' }}>{project.clinicPhone}</a> : null} />
          <Row label="Clinic address" value={project.clinicAddress} />
        </>
      ) : (
        <>
          <div className="ov-grid2">
            <div className="ov-field full"><label className="ov-label">Job Folder URL</label>
              <input className="ov-input" type="url" value={s.f('jobFolderUrl')} onChange={s.set('jobFolderUrl')} placeholder="https://eastandwestbrook.sharepoint.com/…" />
              <div className="ov-hint">Paste the SharePoint URL for this job's folder. Leave blank to clear.</div>
            </div>
            {/* ⚠️ The picker SAVES ON PICK, separately from this card's Save.
                The address carries coordinates, and they have to be written by
                the endpoint that captured them — routing them through the
                card's generic PATCH would store the text and drop the
                location, leaving a 'verified' row with nothing to measure
                from. Picking is therefore its own committed action, which is
                also why the field shows its own status line. */}
            <div className="ov-field full">
              <label className="ov-label">Job Address</label>
              <JobAddressPicker
                value={project.jobAddress ?? ''}
                status={project.jobAddressStatus ?? 'unverified'}
                disabled={!canEdit}
                onPick={async (picked) => {
                  await projectsApi.setAddress(project.id, {
                    line1: picked.line1 ?? '',
                    city: picked.city ?? '',
                    state: picked.state ?? '',
                    postalCode: picked.postalCode ?? undefined,
                    latitude: picked.latitude,
                    longitude: picked.longitude,
                    type: picked.type,
                    matchCodes: picked.matchCodes,
                    // No `expect` here on purpose: this user is looking at the
                    // project's current address, so last-write-wins is correct.
                  });
                  await invalidateAddressQueries(qc, project.id);
                }}
                onClear={async () => {
                  await projectsApi.clearAddress(project.id);
                  await invalidateAddressQueries(qc, project.id);
                }}
              />
            </div>
            <div className="ov-field"><label className="ov-label">Site Contact Name</label><input className="ov-input" value={s.f('siteContactName')} onChange={s.set('siteContactName')} /></div>
            <div className="ov-field"><label className="ov-label">Site Contact Phone</label><input className="ov-input" value={s.f('siteContactPhone')} onChange={s.set('siteContactPhone')} /></div>
            <div className="ov-field"><label className="ov-label">Site Contact Email</label><input className="ov-input" value={s.f('siteContactEmail')} onChange={s.set('siteContactEmail')} /></div>
            <div className="ov-field"><label className="ov-label">Travel Job</label>
              <select className="ov-input" value={s.f('travelJob')} onChange={s.set('travelJob')}>
                <option value="false">No</option><option value="true">Yes</option>
              </select>
            </div>
            {/* ⚠️ A DROPDOWN, AND THE FREE-TEXT NOTE IS GONE FROM THIS FORM.
                The estimator is choosing which band they priced into the bid, and
                the Travel Acknowledgment tool states that band to an employee who
                signs it. Typed, production held '121-500 miles', '121-500  miles',
                '318 miles' and '120+ miles away' — the last matching no band at
                all. The legacy value is still DISPLAYED above when a job has no
                band; it is simply never written again. */}
            <div className="ov-field"><label className="ov-label">Travel Band</label>
              <select className="ov-input" value={s.f('travelBand')} onChange={s.set('travelBand')}>
                <option value="">—</option>
                {TRAVEL_BANDS.map((b) => <option key={b} value={b}>{b} miles</option>)}
              </select>
            </div>
            {/* ⚠️ NOT NESTED UNDER "Travel Job". Prevailing wage is a property
                of the job — a local project can be subject to it too — and it is
                only its effect on travel pay that brings it onto this card.
                Handbook item 6: lodging is unchanged, meals and incidentals and
                the hourly increase both go. */}
            <div className="ov-field"><label className="ov-label">Prevailing Wage</label>
              <select className="ov-input" value={s.f('isPrevailingWage')} onChange={s.set('isPrevailingWage')}>
                <option value="false">No</option><option value="true">Yes</option>
              </select>
            </div>
            <div className="ov-field"><label className="ov-label">Per Diem</label>
              <select className="ov-input" value={s.f('perDiem')} onChange={s.set('perDiem')}>
                <option value="false">No</option><option value="true">Yes</option>
              </select>
            </div>
            {/* ⚠️ "Travel Rotation", not "Per Diem Days" — the column name is a
                misnomer and a free number box invited any value. It is 5 or 7. */}
            <div className="ov-field"><label className="ov-label">Travel Rotation</label>
              <select className="ov-input" value={s.f('perDiemDays')} onChange={s.set('perDiemDays')}>
                <option value="">—</option>
                <option value="5">5 day (home at weekends)</option>
                <option value="7">7 day (stays out)</option>
              </select>
            </div>
            <div className="ov-field"><label className="ov-label">Clinic Name</label><input className="ov-input" value={s.f('clinicName')} onChange={s.set('clinicName')} /></div>
            <div className="ov-field"><label className="ov-label">Clinic Phone</label><input className="ov-input" value={s.f('clinicPhone')} onChange={s.set('clinicPhone')} /></div>
            <div className="ov-field full"><label className="ov-label">Clinic Address</label><input className="ov-input" value={s.f('clinicAddress')} onChange={s.set('clinicAddress')} /></div>
          </div>
          <SaveCancel onCancel={s.cancel} onSave={onSave} saving={s.saving} />
        </>
      )}
    </SectionCard>
  );
}

// ─── Notes (ported from the modal; add / edit-own / delete) ─────────────────

function fmtNoteDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// One note-category card. Per-note canEdit/canDelete are computed server-side
// (author may edit & delete; Admin may delete any; legacy notes are locked).
function ProjectNotes({ projectId, category, label, notes, editable }: {
  projectId: number;
  category: ProjectNote['category'];
  label: string;
  notes: ProjectNote[];
  editable: boolean;
}) {
  const qc = useQueryClient();
  const list = notes.filter((n) => n.category === category);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // The note endpoints return the project's fresh notes list — write it
  // straight into the workspace cache so the change shows immediately even if
  // the follow-up refetch fails (transient auth hiccups were making saved
  // notes look like they vanished). The invalidation still reconciles.
  const after = (notes: ProjectNote[]) => {
    setErr(null);
    qc.setQueryData(['project', projectId], (old: any) => (old ? { ...old, notes } : old));
    qc.invalidateQueries({ queryKey: ['project', projectId] });
  };
  const addMut = useMutation({
    mutationFn: () => projectsApi.addNote(projectId, category, draft.trim()) as Promise<ProjectNote[]>,
    onSuccess: (notes) => { setDraft(''); after(notes); },
    onError: (e: any) => setErr(e?.message ?? 'Failed to add note'),
  });
  const editMut = useMutation({
    mutationFn: (id: number) => projectsApi.updateNote(projectId, id, editText.trim()) as Promise<ProjectNote[]>,
    onSuccess: (notes) => { setEditingId(null); setEditText(''); after(notes); },
    onError: (e: any) => setErr(e?.message ?? 'Failed to save note'),
  });
  const delMut = useMutation({
    mutationFn: (id: number) => projectsApi.deleteNote(projectId, id) as Promise<ProjectNote[]>,
    onSuccess: (notes) => after(notes),
    onError: (e: any) => setErr(e?.message ?? 'Failed to delete note'),
  });

  return (
    <div style={{ background: '#fff', border: '1px solid var(--ew-n-100)', borderRadius: 'var(--ew-r-md)', padding: '14px 16px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.8px', color: 'var(--ew-n-500)', marginBottom: 10 }}>{label}</div>
      {/* Save failures must be unmissable — a quiet one reads as data loss. */}
      {err && (
        <div style={{ fontSize: 12, fontWeight: 600, color: '#8F2414', background: '#FBE4DF', border: '1px solid #F2C7BE', borderRadius: 'var(--ew-r-md)', padding: '8px 10px', marginBottom: 8 }}>
          ⚠ {err} Your text is still below — it has not been saved.
        </div>
      )}

      {list.length === 0 && <p style={{ fontSize: 12, color: 'var(--ew-n-400)', fontStyle: 'italic', margin: '0 0 8px' }}>No notes yet.</p>}

      {list.map((n, i) => (
        <div key={n.id} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: i < list.length - 1 ? '1px solid var(--ew-n-100)' : 'none' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ew)' }}>{n.authorName ?? 'Unknown'}</span>
            <span style={{ fontSize: 10, color: 'var(--ew-n-400)' }}>{fmtNoteDate(n.createdAt)}{n.edited ? ' · edited' : ''}</span>
          </div>
          {editable && editingId === n.id ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea className="ov-note-input" rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="ew-btn-link" style={{ color: 'var(--ew)' }} disabled={!editText.trim() || editMut.isPending} onClick={() => editMut.mutate(n.id)}>
                  {editMut.isPending ? 'Saving…' : 'Save'}
                </button>
                <button className="ew-btn-link" style={{ color: 'var(--ew-n-500)' }} onClick={() => { setEditingId(null); setEditText(''); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--ew-dark)', whiteSpace: 'pre-wrap' }}>{n.body}</div>
              {editable && (n.canEdit || n.canDelete) && (
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  {n.canEdit && <button className="ew-btn-link" style={{ color: 'var(--ew)' }} onClick={() => { setEditingId(n.id); setEditText(n.body); setErr(null); }}>Edit</button>}
                  {n.canDelete && <button className="ew-btn-link" style={{ color: '#B8321C' }} disabled={delMut.isPending} onClick={() => delMut.mutate(n.id)}>Delete</button>}
                </div>
              )}
            </>
          )}
        </div>
      ))}

      {editable && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          <textarea className="ov-note-input" rows={2} placeholder="Add a note…" value={draft} onChange={(e) => setDraft(e.target.value)} />
          <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={!draft.trim() || addMut.isPending} onClick={() => addMut.mutate()}>
            {addMut.isPending ? 'Adding…' : '+ Add note'}
          </button>
        </div>
      )}
    </div>
  );
}

export function NotesSection({ project, canEdit }: { project: OverviewProject; canEdit: boolean }) {
  return (
    <div className="pw-card">
      <div className="ov-sec-hd"><h2 className="ov-sec-title">Notes</h2></div>
      {canEdit ? (
        <div style={{ fontSize: 11, color: 'var(--ew-n-500)', padding: 'var(--ew-s-3) var(--ew-s-4) 0' }}>
          Notes save immediately. You can edit or delete your own notes; older notes (imported, no recorded author) are read-only.
        </div>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, padding: 'var(--ew-s-4)' }}>
        {NOTE_CATEGORIES.map((c) => (
          <ProjectNotes key={c.key} projectId={project.id} category={c.key} label={c.label}
            notes={project.notes} editable={canEdit} />
        ))}
      </div>
    </div>
  );
}

// ─── Danger Zone — archive/restore (editors) + delete (projects.delete only) ──

export function DangerZoneSection({ project }: { project: OverviewProject }) {
  const canDelete = useCanDeleteProject();
  const canEdit = useCanEditProject();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const deleteMutation = useMutation({
    mutationFn: () => projectsApi.delete(project.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpi'] });
      // Deleting your last job takes the Today tab with it; covers `projects` too.
      invalidateAssignmentQueries(qc, project.id);
      navigate('/projects');
    },
  });

  // Archive / restore — mirrors the modal's Danger Zone header action.
  const archiveMutation = useMutation({
    mutationFn: () => projectsApi.update(project.id, { isArchived: !project.isArchived }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kpi'] });
      // Archiving drops the job out of the eligibility predicate; covers
      // `project` and `projects` too.
      invalidateAssignmentQueries(qc, project.id);
    },
  });

  // Archive/restore needs only projects.edit (the backend PATCH enforces that);
  // delete is the projects.delete-gated action. Show the card to either.
  if (!canEdit && !canDelete) return null;

  // Restoring a job Intacct still calls Completed stops the nightly sync archiving it
  // again, so it will stay on the active list until someone archives it by hand.
  // Say so before doing it — Cancel leaves everything as it was.
  const onArchiveClick = () => {
    const warning = restoreWarning(project);
    if (warning && !window.confirm(warning)) return;
    archiveMutation.mutate();
  };

  return (
    <div className="pw-card" style={{ borderColor: '#E7B0A4' }}>
      <div className="ov-sec-hd" style={{ borderBottomColor: '#F2D7D0' }}>
        <h2 className="ov-sec-title" style={{ color: '#8F2414' }}>Danger Zone</h2>
        {canEdit && (
          <button
            className="ew-btn-outline"
            onClick={onArchiveClick}
            disabled={archiveMutation.isPending}
          >
            {project.isArchived ? '↩ Restore' : '📦 Archive'}
          </button>
        )}
      </div>
      <div style={{ padding: 'var(--ew-s-4)' }}>
        <div style={{ fontSize: 12, color: '#8F2414', marginBottom: canDelete ? 8 : 0 }}>
          <strong>Archive</strong> hides <strong>#{project.jobNumber} — {project.title}</strong> from the active
          Projects list; it's fully reversible (Restore brings it back).
          {project.intacctJobStatus && (
            <> The job status in Intacct is <strong>{project.intacctJobStatus}</strong>.</>
          )}
          {canDelete && (
            <> <strong>Delete</strong> is a soft delete — the record is retained and can be restored by an administrator.</>
          )}
        </div>
        {canDelete && (
          <>
            {deleteMutation.isError && (
              <div style={{ fontSize: 12, color: '#8F2414', marginBottom: 8 }}>
                {(deleteMutation.error as Error)?.message ?? 'Delete failed.'}
              </div>
            )}
            {!confirming ? (
              <button onClick={() => setConfirming(true)} className="btn btn-sm" style={{ background: '#B8321C', color: '#fff', border: 'none' }}>
                🗑 Delete project
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label className="ov-label">Type the job number <strong>{project.jobNumber}</strong> to confirm</label>
                <input className="ov-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={project.jobNumber} autoFocus />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => deleteMutation.mutate()}
                    disabled={confirmText.trim() !== project.jobNumber || deleteMutation.isPending}
                    className="btn btn-sm"
                    style={{
                      background: confirmText.trim() === project.jobNumber ? '#B8321C' : '#E7B0A4',
                      color: '#fff', border: 'none',
                      cursor: confirmText.trim() === project.jobNumber ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Delete project'}
                  </button>
                  <button onClick={() => { setConfirming(false); setConfirmText(''); }} className="btn btn-secondary btn-sm">Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Style injector (mount once from the Overview) ──────────────────────────

export function OverviewEditStyles() {
  return <style>{css}</style>;
}
